import React, { startTransition, useEffect, useState } from 'react';
import DataTable, { FilterConfig } from '../components/DataTable';
import ViewToggle from '../components/ViewToggle';
import { qrService, QrProducto } from '../services/qrService';

const QRsPage: React.FC = () => {
  const [data, setData] = useState<QrProducto[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [viewSwitching, setViewSwitching] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const productos = await qrService.getProductos();
        setData(productos);
      } catch (error) {
        console.error('Error al cargar QRs:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const productos = await qrService.getProductos();
      setData(productos);
    } catch (error) {
      console.error('Error al recargar QRs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (next: 'grid' | 'table') => {
    if (next === viewMode) return;
    setViewSwitching(true);
    startTransition(() => {
      setViewMode(next);
    });
    // Mantener overlay hasta que el browser pueda pintar la nueva vista
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setViewSwitching(false);
      });
    });
  };

  const handleGenerate = async () => {
    try {
      await qrService.generateCodes();
      await reload();
    } catch (error) {
      console.error('Error al generar códigos:', error);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const columns = [
    { key: 'codigo', label: 'QR' },
    { key: 'cliente', label: 'Cliente' },
    { key: 'sucursal', label: 'Sucursal' },
    { key: 'tipoProducto', label: 'Tipo de producto' },
    { key: 'tipoElemento', label: 'Tipo de inst. fija' },
    { key: 'producto', label: 'Producto' },
  ];

  const filterConfig: FilterConfig[] = [
    {
      key: 'cliente',
      label: 'Cliente',
      type: 'select',
    },
    {
      key: 'sucursal',
      label: 'Sucursal',
      type: 'select',
    },
    {
      key: 'tipoProducto',
      label: 'Tipo de producto',
      type: 'select',
    },
    {
      key: 'tipoElemento',
      label: 'Tipo de instalación fija',
      type: 'select',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">QRs</h1>
          <p className="text-gray-600 dark:text-gray-400">Listado de etiquetas QR de productos</p>
        </div>
        <div className="flex items-center space-x-4">
          <ViewToggle currentView={viewMode} onViewChange={handleViewChange} />
          <div className="flex gap-2">
            <button
              onClick={handleGenerate}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
            >
              Generar códigos
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
            >
              Imprimir
            </button>
          </div>
        </div>
      </div>

      <DataTable
        data={data}
        columns={columns}
        viewMode={viewMode}
        loading={viewSwitching}
        searchPlaceholder="Buscar por sucursal..."
        filters={filterConfig}
        enableAdvancedFilters={true}
        itemsPerPage={12}
      />
    </div>
  );
};

export default QRsPage;
